// tslint:disable:no-any
import { Observable, concat, of } from 'rxjs';
import { SlackReactionId, SlackReactionTs, SlackReactionType } from '../value';
import { count, filter, map, mergeMap, take, tap } from 'rxjs/operators';

import { FirestoreQueryBuilder } from '../../../../lib/gcp/builder/firestore-query.builder';
import { ISlackReactionRepository } from '../repository';
import { SlackMessageId } from '../../slack-message/value';
import { SlackReaction } from '../entity';
import { SlackReactionAlreadyExistsError, SlackReactionNotFoundError } from '../exception';
import { SlackReactionDto } from '../dto';
import { SlackUserId } from '../../slack-user/value';
import { Timestamp } from '../../../../utility/model/timestamp.value';

export class SlackReactionDomainService {
  constructor(private readonly slackReactionRepository: ISlackReactionRepository) { }

  insertSlackReaction(userId: string, messageId: string, type: string, ts: string): Observable<SlackReactionDto> {
    return concat(
      this.slackReactionRepository.selectAll(
        new FirestoreQueryBuilder<SlackReaction>().equalWhere('userId', userId).equalWhere('messageId', messageId).equalWhere('type', type)
      ),
      of(null)
    ).pipe(
      take(1),
      tap(slackMessage => {
        if (slackMessage !== null) {
          throw new SlackReactionAlreadyExistsError('this reaction aleady exists');
        }
      }),
      map(_ => {
        const slackReaction = new SlackReaction(this.slackReactionRepository.generateId());
        slackReaction.userId = SlackUserId.create(userId);
        slackReaction.messageId = SlackMessageId.create(messageId);
        slackReaction.type = SlackReactionType.create(type);
        slackReaction.ts = SlackReactionTs.create(ts);
        return slackReaction;
      }),
      mergeMap(slackMessage => this.slackReactionRepository.insert(slackMessage)),
      map(item => this.convertDto(item))
    );
  }

  selectSlackReactionByUserIdAndMessageIdAndType(userId: string, messageId: string, type: string) {
    return concat(
      this.slackReactionRepository.selectAll(
        new FirestoreQueryBuilder<SlackReaction>().equalWhere('userId', userId).equalWhere('messageId', messageId).equalWhere('type', type)
      ),
      of(null)
    ).pipe(
      take(1),
      tap(slackMessage => {
        if (slackMessage === null) {
          throw new SlackReactionNotFoundError('this slack reaction is not found');
        }
      }),
      map(item => this.convertDto(item))
    );
  }

  countOfSlackReactionByMessageIdAndReactionType(messageId: string, type: string) {
    return this.slackReactionRepository
      .selectAll(new FirestoreQueryBuilder<SlackReaction>()
        .equalWhere('messageId', messageId)
        .equalWhere('type', type))
      .pipe(count());
  }

  selectLatestSlackReactionByMessageIdAndReactionType(messageId: string, type: string, limit: number = 1) {
    return this.slackReactionRepository
      .selectAll(new FirestoreQueryBuilder<SlackReaction>()
        .equalWhere('messageId', messageId)
        .equalWhere('type', type)
        .orderBy('createdAt', 'desc')
        .limit(limit)
      )
      .pipe(
        map(item => this.convertDto(item)),
        // filter(item => item && item.messageId === messageId && item.type === type),
      );
  }

  selectAllSlackReactionByMessageIdAndReactionType(messageId: string, type: string, limit: number = 1000) {
    return this.slackReactionRepository
      .selectAll(new FirestoreQueryBuilder<SlackReaction>()
        .equalWhere('messageId', messageId)
        .equalWhere('type', type)
        .orderBy('createdAt', 'asc')
        .limit(limit)
      )
      .pipe(
        map(item => this.convertDto(item)),
        // filter(item => item && item.messageId === messageId && item.type === type),
      );
  }

  updateSlacReaction(slackReactionDto: SlackReactionDto) {
    const slackReaction = new SlackReaction(SlackReactionId.create(slackReactionDto.id));
    slackReaction.userId = SlackUserId.create(slackReactionDto.userId);
    slackReaction.messageId = SlackMessageId.create(slackReactionDto.messageId);
    slackReaction.type = SlackReactionType.create(slackReactionDto.type);
    slackReaction.ts = SlackReactionTs.create(slackReactionDto.ts);
    slackReaction.createdAt = Timestamp.createByDate(slackReactionDto.createdAt);
    return this.slackReactionRepository.update(slackReaction).pipe(map(item => this.convertDto(item)));
  }

  deleteSlackReaction(id: string) {
    return this.slackReactionRepository.delete(SlackReactionId.create(id));
  }

  private convertDto(slackReaction: SlackReaction): SlackReactionDto {
    return SlackReaction.allFields.reduce((p, key) => {
      if (slackReaction[key] === undefined) {
        return p;
      }
      const value = slackReaction[key] as { value: any };
      p[key] = value.value;
      return p;
    }, {} as SlackReactionDto);
  }
}
